import { ZapIcon } from '@persona-id/design-tokens/icons';
import { memo, ReactNode, useMemo } from 'react';
import styled from 'styled-components';

import ActionSequencePreview from 'dashboard/apps/InquiryTemplateEditor/ActionSequencePreview';
import { getActionNodeSteps } from 'dashboard/apps/InquiryTemplateEditor/TemplateWorkflowEditor/utils';
import { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import { WorkflowStep } from 'dashboard/models/workflow-version';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { Elevation } from 'design-system/variables/effect';

export const ACTIONS_NODE_SIZE = 162;

const NodeButton = styled(Button).attrs(() => ({ type: 'button' }))<{
  selected: boolean;
  hasError?: boolean;
  isPlaceholder?: boolean;
}>`
  display: grid;
  place-items: center;
  cursor: pointer;
  pointer-events: auto;
  user-select: none;
  width: ${ACTIONS_NODE_SIZE}px;
  height: ${ACTIONS_NODE_SIZE}px;
  box-shadow: ${Elevation.Mid};
  box-sizing: border-box;
  border: 2px solid;
  border-radius: ${({ theme }) => theme.card.borderRadius};
  z-index: ${Z_INDICES.withShadow};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-style: ${({ isPlaceholder }) => (isPlaceholder ? 'dashed' : null)};
  border-color: ${({ theme, selected, hasError }) => {
    if (hasError) {
      return theme.color.variants.danger.default;
    } else if (selected) {
      return theme.color.primary.brand600;
    } else {
      return theme.color.variants.primary.highlight;
    }
  }};
  svg {
    color: ${({ theme }) => theme.color.primary.brand600};
    height: 48px;
    width: 44px;
  }
  &:focus:enabled {
    background-color: ${({ theme }) => theme.color.background.primary};
  }
`;

export interface ActionsNodeProps {
  title: string;
  workflowVersionId: string;
  workflowStepToken: string;
  workflowSteps?: WorkflowStep[];
  editable?: boolean;
  selected?: boolean;
  hasError?: boolean;
  isPlaceholder?: boolean;
  onActionsDoubleClick: (workflowVersionId: string, workflowStepToken: string) => void;
  nodeHighlighter?: ReactNode;
}

/**
 * This component is responsible for rendering an ActionsNode
 * representing a linear sequence of Dynamic Flow template workflow steps
 */
const ActionsNode = ({
  title,
  workflowVersionId,
  workflowStepToken,
  workflowSteps,
  editable,
  selected,
  hasError,
  isPlaceholder,
  onActionsDoubleClick,
  nodeHighlighter,
}: ActionsNodeProps) => {
  const panZoomBoxControls = usePanZoomBoxControls();
  const isTransforming = panZoomBoxControls?.isTransforming ?? false;
  const actionNodeSteps = useMemo(
    () => getActionNodeSteps(workflowSteps ?? [], workflowStepToken),
    [workflowSteps, workflowStepToken],
  );
  const showHoverPreview = actionNodeSteps.length > 0;
  return (
    <Tooltip
      side={showHoverPreview ? 'right' : 'top'}
      trigger={
        <NodeButton
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          selected={selected}
          hasError={hasError}
          isPlaceholder={isPlaceholder}
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onActionsDoubleClick(workflowVersionId, workflowStepToken);
          }}
        >
          {nodeHighlighter}
          <ZapIcon size={24} />
        </NodeButton>
      }
      disabled={isTransforming}
    >
      {showHoverPreview ? (
        <ActionSequencePreview
          title={title}
          steps={actionNodeSteps}
          workflowVersionId={workflowVersionId}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          editable={editable}
        />
      ) : (
        <Paragraph>{title}</Paragraph>
      )}
    </Tooltip>
  );
};

export default memo(ActionsNode);
